import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileText, Radar, BadgePoundSterling, CheckCircle2 } from "lucide-react";
import { Section, Glass, Reveal, Swap, useAudience } from "./NetworkAtoms";
import { WatchExplainerButton } from "./ExplainerVideo";
import CodeRain from "@/components/marketing/CodeRain";
import { useCodeRain } from "@/lib/siteEffects";

const COPY = {
  introducer: {
    title: "Thirty seconds, then you'll understand the whole thing",
    lede: "No portals to integrate, no spreadsheets, no chasing. Three moves and you're done.",
    beats: [
      { Icon: FileText, k: "You introduce", body: "Submit it in your portal in two minutes — or send your client your own link and let them complete it themselves, documents and all." },
      { Icon: Radar, k: "We match and route", body: "Every case is fit-scored against live lender criteria and placed with the desks that fund that profile. You watch it move, in real time." },
      { Icon: BadgePoundSterling, k: "You get paid", body: "Commission is calculated from your live terms, statemented automatically as a PDF, and paid on your schedule." },
    ],
  },
  lender: {
    title: "Thirty seconds, then you'll understand the whole thing",
    lede: "No integration project, no data cleanse, no unqualified noise. Three moves and it's on your desk.",
    beats: [
      { Icon: FileText, k: "A vetted introducer submits", body: "Agreement signed, NDA in place, case structured in one shape — amount, term, sector, purpose, documents, consent, timestamped." },
      { Icon: Radar, k: "We route it to your criteria", body: "You set the band, sectors, terms and geography. Fit-scoring means you can see why a case reached you before you open it." },
      { Icon: CheckCircle2, k: "You decide, we do the rest", body: "Move it through review, offer and completion in the portal or your CRM. Introducer comms, chasing and commission are handled off your desk." },
    ],
  },
};

export default function ThirtySecond() {
  const { audience } = useAudience();
  const c = COPY[audience];
  const rain = useCodeRain("lending_network", "dark");
  return (
    <div className="relative overflow-hidden">
      {rain.on && <CodeRain variant="dark" intensity={rain.intensity} />}
      <div className="relative">
    <Section eyebrow="How it works" testId="network-thirty-second"
             title={<Swap k={`ts-t-${audience}`}>{c.title}</Swap>}>
      <Swap k={`ts-l-${audience}`} className="mt-4 max-w-2xl text-[15px] sm:text-base text-slate-400">
        {c.lede}
      </Swap>

      <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-3">
        <span className="text-[13px] text-slate-500">Prefer to watch it?</span>
        <WatchExplainerButton inline testId="thirty-second-watch-explainer" />
      </div>

      <div className="mt-12 grid md:grid-cols-3 gap-5">
        {c.beats.map((b, i) => (
          <Reveal key={b.k} delay={i * 0.09}>
            <Glass className="h-full p-6 sm:p-7 group" testId={`beat-${i}`}>
              <div className="flex items-center justify-between">
                <span className="grid place-items-center size-11 rounded-xl border border-white/10 bg-[#0055FF]/[0.12] text-[#7da9ff]">
                  <b.Icon className="size-5" strokeWidth={1.6} />
                </span>
                <span className="font-mono text-[11px] text-slate-600">0{i + 1}</span>
              </div>
              <h3 className="mt-5 text-[17px] font-semibold text-white tracking-[-0.01em]">{b.k}</h3>
              <p className="mt-2 text-[13.5px] leading-relaxed text-slate-400">{b.body}</p>
              <Micro index={i} audience={audience} />
            </Glass>
          </Reveal>
        ))}
      </div>
    </Section>
      </div>
    </div>
  );
}

/** A small living diagram per beat — form filling, fit bars, payout ticking. */
function Micro({ index, audience }) {
  const reduce = useReducedMotion();
  const anim = (props) => (reduce ? {} : props);

  if (index === 0) {
    return (
      <div className="mt-6 space-y-2" aria-hidden>
        {[82, 64, 94, 48].map((w, i) => (
          <div key={i} className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
            <motion.div className="h-full rounded-full bg-[#7da9ff]/70"
                        initial={{ width: 0 }}
                        {...anim({ whileInView: { width: `${w}%` } })}
                        viewport={{ once: true }}
                        transition={{ duration: 1.1, delay: 0.15 * i, ease: [0.16, 1, 0.3, 1] }} />
          </div>
        ))}
      </div>
    );
  }

  if (index === 1) {
    const bars = [92, 74, 61, 38];
    return (
      <div className="mt-6 flex items-end gap-2 h-16" aria-hidden>
        {bars.map((h, i) => (
          <div key={i} className="flex-1 h-full flex flex-col items-center justify-end gap-1.5">
            <motion.div
              className={`w-full rounded-t-md ${i === 0 ? "bg-[#0055FF]" : "bg-white/[0.12]"}`}
              initial={{ height: 4 }}
              {...anim({ whileInView: { height: `${h}%` } })}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.12 * i, ease: [0.16, 1, 0.3, 1] }} />
            <span className={`font-mono text-[9.5px] ${i === 0 ? "text-[#7da9ff]" : "text-slate-600"}`}>{h}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="mt-6" aria-hidden>
      <div className="rounded-xl border border-white/[0.08] bg-[#0B1220]/60 p-3.5">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-slate-500">
            {audience === "lender" ? "Completion" : "Statement · September"}
          </span>
          <span className="text-[10px] text-emerald-400 font-semibold">
            {audience === "lender" ? "Funded" : "Paid"}
          </span>
        </div>
        <motion.div className="mt-2 font-mono tabular-nums text-[20px] font-semibold text-white"
                    initial={{ opacity: 0.35 }}
                    {...anim({ whileInView: { opacity: 1 } })}
                    viewport={{ once: true }} transition={{ duration: 0.8 }}>
          {audience === "lender" ? "£1,850,000" : "£27,750"}
        </motion.div>
        <div className="mt-2 h-1 rounded-full bg-white/[0.07] overflow-hidden">
          <motion.div className="h-full bg-emerald-400/80" initial={{ width: 0 }}
                      {...anim({ whileInView: { width: "100%" } })}
                      viewport={{ once: true }} transition={{ duration: 1.4, ease: "easeOut" }} />
        </div>
      </div>
    </div>
  );
}
